import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Input, InputGroup, Panel, Stack, Table,
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import { RiSettings5Fill } from 'react-icons/ri';
import { Icon } from '@rsuite/icons';
import { useStoreState } from '../store/hooks';
import { getPartners } from '../api/partner/partner';
import { Partner } from '../api/partner/partnerApiTypes';
import PartnerCreate from '../components/PartnerSetup/Create/PartnerCreate';
import { Permission } from '../store/user/types';

interface Props {
  onSelection: (partnerCode: string) => void;
  showCreatePartner?: boolean;
}

function filterPartners(partners: Partner[], filter: string): Partner[] {
  const lowerCasedFilter = filter.toLowerCase();

  return partners.filter((partner) => {
    const values = [
      partner.id,
      partner.code,
      partner.name,
      partner.integration,
      partner.workflowStatus,
    ];

    return values
      .map(value => String(value).toLowerCase())
      .some(value => value.includes(lowerCasedFilter));
  });
}

export default function PartnersTable({ onSelection, showCreatePartner }: Props) {
  const { hasPermission } = useStoreState(state => state.user);
  const [loading, setLoading] = useState<boolean>(true);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [filterValue, setFilterValue] = useState<string>('');
  const [appliedFilter, setAppliedFilter] = useState<string>('');
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);

  const canCreatePartner = showCreatePartner
    && hasPermission(Permission.PartnerSetupOverviewCreate);

  const filteredPartners = useMemo(
    () => filterPartners(partners, appliedFilter),
    [partners, appliedFilter],
  );

  function fetchPartners() {
    setLoading(true);

    getPartners()
      .then(data => {
        setPartners(data);
      }).finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchPartners();
  }, []);

  function search() {
    setAppliedFilter(filterValue);
  }

  function handleFilterKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      search();
    }
  }

  return (
    <div>
      <Panel className="h100p">
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <InputGroup inside>
            <Input
              style={{ width: 400 }}
              placeholder="search by id, code, name, integration, workflow status"
              defaultValue={filterValue}
              onChange={setFilterValue}
              onKeyUp={(event) => handleFilterKeyUp(event)}
            />
            <InputGroup.Button>
              <SearchIcon onClick={() => search()} />
            </InputGroup.Button>
          </InputGroup>
          {canCreatePartner && (
            <Button appearance="primary" onClick={() => setIsCreateOpen(true)}>
              Create new partner
            </Button>
          )}
        </Stack>
        <Table
          autoHeight
          bordered
          data={filteredPartners}
          loading={loading}
          className="mt-20"
        >
          <Table.Column width={50} align="center">
            <Table.HeaderCell>Id</Table.HeaderCell>
            <Table.Cell dataKey="id" />
          </Table.Column>
          <Table.Column width={100} align="center">
            <Table.HeaderCell>Code</Table.HeaderCell>
            <Table.Cell dataKey="code" />
          </Table.Column>
          <Table.Column width={200} align="center">
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.Cell dataKey="name" />
          </Table.Column>
          <Table.Column flexGrow={2} align="center">
            <Table.HeaderCell>Integration</Table.HeaderCell>
            <Table.Cell dataKey="integration" />
          </Table.Column>
          <Table.Column flexGrow={3} align="center">
            <Table.HeaderCell>Marketplace count (total/active)</Table.HeaderCell>
            <Table.Cell>
              {(rowData: Partner) => `${rowData.marketplaceCountTotal}/${rowData.marketplaceCountActive}`}
            </Table.Cell>
          </Table.Column>
          <Table.Column flexGrow={3} align="center">
            <Table.HeaderCell>Workflow status</Table.HeaderCell>
            <Table.Cell dataKey="workflowStatus" />
          </Table.Column>
          <Table.Column flexGrow={10} align="left">
            <Table.HeaderCell>Webhook</Table.HeaderCell>
            <Table.Cell dataKey="webhook" />
          </Table.Column>
          <Table.Column align="center">
            <Table.HeaderCell>Set up</Table.HeaderCell>
            <Table.Cell dataKey="code">
              {rowData => (
                <Icon
                  as={RiSettings5Fill}
                  onClick={() => onSelection(rowData.code)}
                  style={{ width: 27, height: 27 }}
                />
              ) }
            </Table.Cell>
          </Table.Column>
        </Table>
      </Panel>
      {canCreatePartner && (
        <PartnerCreate
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreated={() => fetchPartners()}
        />
      )}
    </div>
  );
}

PartnersTable.defaultProps = {
  showCreatePartner: false,
};
